/* eslint-disable */
// Vendor-side My Work Orders — list + read-only viewer.
// Replaces the "click row in Finance to open" UX request — vendors get a
// dedicated sidebar entry. Uses the new `GET /api/work-orders/{id}` endpoint
// added in Session 79 which is role-aware (vendors only see their own WOs).
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft, FileText, CheckCircle2, Clock, XCircle, Eye, Calendar, Receipt,
  Download,
} from 'lucide-react';
import { downloadWorkOrderPdf } from '@/lib/woPdfDownload';

const STATUS_PILL = {
  issued:    { label: 'ISSUED · Awaiting Acceptance', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  accepted:  { label: 'ACCEPTED',                     cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
  closed:    { label: 'CLOSED',                       cls: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/40' },
  cancelled: { label: 'CANCELLED',                    cls: 'bg-rose-500/15 text-rose-300 border-rose-500/40' },
};

function fmtINR(n) {
  if (n === null || n === undefined) return '—';
  return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

export default function VendorWorkOrdersPage() {
  const { api } = useAuth();
  const [wos, setWos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | issued | accepted | closed
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get('/work-orders')
      .then(r => setWos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setWos([]))
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    api.get(`/work-orders/${selectedId}`)
      .then(r => setDetail(r.data))
      .catch(() => setDetail(null));
  }, [selectedId, api]);

  const counts = useMemo(() => {
    const c = { all: wos.length, issued: 0, accepted: 0, closed: 0, cancelled: 0 };
    for (const w of wos) c[w.status] = (c[w.status] || 0) + 1;
    return c;
  }, [wos]);

  const visible = useMemo(() => {
    return filter === 'all' ? wos : wos.filter(w => w.status === filter);
  }, [wos, filter]);

  if (selectedId && detail) {
    return <WoDetailView
      wo={detail}
      onBack={() => { setSelectedId(null); setDetail(null); }}
      onAccepted={(updatedWo) => {
        // Refresh detail immediately with the new status so the UI flips
        // ISSUED → ACCEPTED without a round-trip flash. Then re-fetch the
        // list so the count badges update.
        setDetail(updatedWo);
        setWos(prev => prev.map(w => w.id === updatedWo.id ? { ...w, ...updatedWo } : w));
      }}
    />;
  }

  return (
    <div className="space-y-4" data-testid="vendor-work-orders-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-white">My Work Orders</h1>
        <p className="text-xs text-zinc-500 mt-1">All Work Orders issued to you — past, current, and accepted.</p>
      </div>

      {/* Status filter chips */}
      <div className="flex gap-2 flex-wrap">
        {[
          { k: 'all',       label: 'All' },
          { k: 'issued',    label: 'Awaiting Acceptance' },
          { k: 'accepted',  label: 'Accepted' },
          { k: 'closed',    label: 'Closed' },
          { k: 'cancelled', label: 'Cancelled' },
        ].map(f => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`vendor-wo-filter-${f.k}`}
            className={`px-3 h-8 rounded-full text-[11px] font-medium border transition-colors ${
              filter === f.k
                ? 'bg-amber-500/20 border-amber-400/60 text-amber-200'
                : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-zinc-200'
            }`}
          >
            {f.label}
            <span className="ml-1.5 text-[9px] opacity-70 font-mono">{counts[f.k] || 0}</span>
          </button>
        ))}
      </div>

      {/* List */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
        {loading ? (
          <div className="p-8 text-center text-xs text-zinc-500 animate-pulse">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-sm text-zinc-400">No Work Orders {filter !== 'all' ? `with status ${filter}` : 'yet'}</p>
            <p className="text-[10px] text-zinc-600 mt-1">Once VawWorld issues a WO to you, it will appear here.</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800/40">
            {visible.map(w => {
              const pill = STATUS_PILL[w.status] || STATUS_PILL.issued;
              return (
                <button
                  key={w.id}
                  onClick={() => setSelectedId(w.id)}
                  data-testid={`vendor-wo-row-${w.id}`}
                  className="w-full text-left px-4 py-3 hover:bg-zinc-900/60 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[11px] text-zinc-400">{w.work_order_no}</span>
                        <span className={`text-[9px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 border ${pill.cls}`}>{pill.label}</span>
                      </div>
                      <p className="text-sm text-white font-medium mt-1 truncate">{w.scope_title || w.scope?.scope_title || 'Untitled scope'}</p>
                      <p className="text-[10px] text-zinc-500 mt-0.5 truncate">
                        {w.project_name || ''} {w.project_info?.client_name ? `· ${w.project_info.client_name}` : ''}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-mono text-amber-300">{fmtINR(w.total_amount)}</p>
                      <p className="text-[9px] text-zinc-600">incl. {w.tax?.gst_percent || 0}% GST</p>
                      {w.accepted_at && <p className="text-[9px] text-emerald-400 mt-1">Accepted {new Date(w.accepted_at).toLocaleDateString()}</p>}
                    </div>
                    <Eye className="w-4 h-4 text-zinc-600 mt-1" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────── DETAIL VIEW ───────────────────────────
function WoDetailView({ wo, onBack, onAccepted }) {
  const { api } = useAuth();
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfErr, setPdfErr] = useState('');
  // ── Accept Work Order (May 20 2026) ──
  // Founder report: vendors were unable to accept WOs because the Accept
  // button was missing from this viewer entirely (only existed on the
  // freelancer dashboard). Production was halted for Soniya Lamba etc.
  const [acceptBusy, setAcceptBusy] = useState(false);
  const [acceptErr, setAcceptErr] = useState('');
  const pill = STATUS_PILL[wo.status] || STATUS_PILL.issued;
  const onDownload = async () => {
    setPdfBusy(true);
    setPdfErr('');
    try {
      await downloadWorkOrderPdf(api, wo);
    } catch (e) {
      setPdfErr('Could not download PDF — please try again');
    } finally {
      setPdfBusy(false);
    }
  };
  const onAccept = async () => {
    if (!window.confirm(`Accept Work Order ${wo.work_order_no}?\n\nThis confirms the scope, deliverables, and total of ${fmtINR(wo.total_amount)}. Once accepted, the studio is notified and work can begin.`)) return;
    setAcceptBusy(true);
    setAcceptErr('');
    try {
      const r = await api.put(`/work-orders/${wo.id}/status`, { status: 'accepted' });
      const updated = r?.data?.work_order || r?.data || { ...wo, status: 'accepted', accepted_at: new Date().toISOString() };
      onAccepted?.(updated);
    } catch (e) {
      setAcceptErr(e.response?.data?.detail || 'Could not accept Work Order. Please try again.');
    } finally {
      setAcceptBusy(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="vendor-wo-detail">
      <div className="flex items-center justify-between gap-3">
        <button onClick={onBack} data-testid="vendor-wo-back" className="text-zinc-400 hover:text-white flex items-center gap-1 text-xs">
          <ArrowLeft className="w-4 h-4" /> Back to Work Orders
        </button>
        <button
          onClick={onDownload}
          disabled={pdfBusy}
          data-testid="vendor-wo-download-pdf"
          className="h-8 px-3 rounded-sm border border-amber-500/50 bg-amber-500/10 text-amber-200 text-[11px] uppercase tracking-wider flex items-center gap-1.5 hover:bg-amber-500/20 disabled:opacity-50"
        >
          <Download className="w-3.5 h-3.5" /> {pdfBusy ? 'Preparing…' : 'Download PDF'}
        </button>
      </div>
      {pdfErr && <p className="text-[10px] text-rose-400" data-testid="vendor-wo-download-error">{pdfErr}</p>}

      {/* ── Accept banner — only when WO is awaiting acceptance ── */}
      {wo.status === 'issued' && (
        <div
          className="bg-emerald-500/10 border border-emerald-500/40 rounded-sm p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          data-testid="vendor-wo-accept-banner"
        >
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-emerald-300 font-bold">Action required — Accept Work Order</p>
            <p className="text-[11px] text-emerald-200/70 mt-0.5">
              Review the scope, rate card, and total of <span className="font-mono font-semibold text-emerald-200">{fmtINR(wo.total_amount)}</span> below. Once you click Accept the studio is notified and work begins.
            </p>
          </div>
          <button
            onClick={onAccept}
            disabled={acceptBusy}
            data-testid="vendor-wo-accept-btn"
            className="shrink-0 h-9 px-4 rounded-sm border border-emerald-400 bg-emerald-500 text-zinc-950 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-wait"
          >
            <CheckCircle2 className="w-4 h-4" /> {acceptBusy ? 'Accepting…' : 'Accept Work Order'}
          </button>
        </div>
      )}
      {acceptErr && <p className="text-[10px] text-rose-400" data-testid="vendor-wo-accept-error">{acceptErr}</p>}

      {/* Header card */}
      <div className="bg-zinc-900 border border-amber-500/40 rounded-sm p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-zinc-400">{wo.work_order_no}</span>
              <span className={`text-[9px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 border ${pill.cls}`}>{pill.label}</span>
            </div>
            <h2 className="text-xl font-heading font-bold text-white mt-1">{wo.scope_title || 'Untitled'}</h2>
            <p className="text-xs text-zinc-500 mt-1">
              {wo.project_name || ''} {wo.project_info?.client_name ? `· Client: ${wo.project_info.client_name}` : ''}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-mono font-bold text-amber-300">{fmtINR(wo.total_amount)}</p>
            <p className="text-[10px] text-zinc-500">Subtotal {fmtINR(wo.subtotal)} + GST {wo.tax?.gst_percent || 0}%</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs">
          <Field label="Issued" value={wo.created_at ? new Date(wo.created_at).toLocaleDateString() : '—'} icon={Calendar} />
          <Field label="Accepted" value={wo.accepted_at ? new Date(wo.accepted_at).toLocaleDateString() : '—'} icon={CheckCircle2} />
          <Field label="Start Date" value={wo.project_info?.start_date || '—'} icon={Clock} />
          <Field label="End Date" value={wo.project_info?.end_date || '—'} icon={Clock} />
        </div>
      </div>

      {/* Rate card */}
      {(wo.rate_card?.length || wo.line_items?.length) > 0 && (
        <Section title="Rate Card / Deliverables" icon={Receipt}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                  <th className="py-2 px-3">Section</th>
                  <th className="py-2 px-3">Task</th>
                  <th className="py-2 px-3 text-right">Qty</th>
                  <th className="py-2 px-3 text-right">Rate</th>
                  <th className="py-2 px-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(wo.rate_card || wo.line_items || []).map((li, i) => (
                  <tr key={i} className="border-b border-zinc-800/30">
                    <td className="py-2 px-3 text-zinc-400">{li.section || '—'}</td>
                    <td className="py-2 px-3 text-zinc-200">{li.task || li.label || '—'}</td>
                    <td className="py-2 px-3 text-right font-mono text-zinc-300">{li.quantity || 1} {li.unit || ''}</td>
                    <td className="py-2 px-3 text-right font-mono text-zinc-300">{fmtINR(li.rate)}</td>
                    <td className="py-2 px-3 text-right font-mono text-amber-300">{fmtINR(li.amount || (li.rate * (li.quantity || 1)))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Milestones */}
      {wo.payment_terms?.milestones?.length > 0 && (
        <Section title="Payment Milestones">
          <div className="grid md:grid-cols-3 gap-3">
            {wo.payment_terms.milestones.map((m, i) => (
              <div key={i} className="border border-zinc-800 rounded-sm p-3 bg-zinc-950/40">
                <p className="text-xs font-semibold text-amber-300">{m.name}</p>
                <p className="text-[10px] text-zinc-500 mt-1">{m.trigger}</p>
                <p className="text-sm font-mono text-white mt-2">{m.percent}% · {fmtINR((wo.total_amount || 0) * (m.percent || 0) / 100)}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Scope */}
      {(wo.scope?.selected_deliverables?.length || wo.scope_exclusions?.length) > 0 && (
        <Section title="Scope">
          {wo.scope?.selected_deliverables?.length > 0 && (
            <>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Selected Deliverables</p>
              <ul className="space-y-1">
                {wo.scope.selected_deliverables.map((d, i) => (
                  <li key={i} className="text-xs text-zinc-300">• <span className="font-mono text-cyan-400">{d.code || d.id}</span> {d.name && `— ${d.name}`}</li>
                ))}
              </ul>
            </>
          )}
          {wo.scope_exclusions?.length > 0 && (
            <>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-3 mb-2">Out of Scope</p>
              <ul className="space-y-1">
                {wo.scope_exclusions.map((e, i) => (
                  <li key={i} className="text-xs text-zinc-400">• {e}</li>
                ))}
              </ul>
            </>
          )}
        </Section>
      )}

      {/* Legal terms */}
      {wo.legal_terms && (
        <Section title="Legal & Commercial Terms">
          <div className="space-y-2 text-xs">
            {wo.legal_terms.ip_ownership && (<p className="text-zinc-300"><b className="text-zinc-500">IP Ownership:</b> {wo.legal_terms.ip_ownership}</p>)}
            {wo.legal_terms.usage_rights && (<p className="text-zinc-300"><b className="text-zinc-500">Usage Rights:</b> {wo.legal_terms.usage_rights}</p>)}
            {wo.legal_terms.confidentiality && (<p className="text-zinc-300"><b className="text-zinc-500">Confidentiality:</b> {wo.legal_terms.confidentiality}</p>)}
            {wo.legal_terms.delay_penalty && (<p className="text-zinc-300"><b className="text-zinc-500">Delay Penalty:</b> {wo.legal_terms.delay_penalty}</p>)}
            {wo.legal_terms.termination && (<p className="text-zinc-300"><b className="text-zinc-500">Termination:</b> {wo.legal_terms.termination}</p>)}
          </div>
        </Section>
      )}

      {wo.notes && (
        <Section title="Additional Notes">
          <p className="text-xs text-zinc-300 whitespace-pre-wrap">{wo.notes}</p>
        </Section>
      )}

      {/* Re-issue history (if any) */}
      {wo.reissue_history?.length > 0 && (
        <Section title="Re-issue History">
          <ul className="space-y-2">
            {wo.reissue_history.map((h, i) => (
              <li key={i} className="text-xs text-zinc-300 border-l-2 border-amber-500/40 pl-3">
                <span className="text-amber-300">{new Date(h.at).toLocaleString()}</span>
                {h.reason && <span className="text-zinc-400"> · {h.reason}</span>}
                {h.previous_total != null && <span className="text-zinc-500 ml-1">(was {fmtINR(h.previous_total)})</span>}
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

const Field = ({ label, value, icon: Icon }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
      {Icon && <Icon className="w-3 h-3" />} {label}
    </p>
    <p className="text-xs text-zinc-200 mt-0.5">{value}</p>
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
    <h3 className="text-xs uppercase tracking-wider text-amber-300 font-semibold flex items-center gap-1.5 mb-3">
      {Icon && <Icon className="w-3.5 h-3.5" />} {title}
    </h3>
    {children}
  </div>
);
